import { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const ACTION_STYLES = {
  'admin.login.success': 'bg-[#24135F]/5 text-[#24135F] border-[#24135F]/20',
  'admin.login.failed': 'bg-[#E4002B]/5 text-[#E4002B] border-[#E4002B]/25',
  'admin.logout': 'bg-[#D9D9D6]/15 text-[#53565A] border-[#D9D9D6]',
  'vehicle.created': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
  'vehicle.updated': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
  'vehicle.deleted': 'bg-[#E4002B]/5 text-[#E4002B] border-[#E4002B]/25',
  'vehicle.marked_sold': 'bg-[#D9D9D6]/25 text-[#53565A] border-[#53565A]/25',
  'vehicle.marked_available': 'bg-[#24135F]/5 text-[#24135F] border-[#24135F]/20',
  'image.uploaded': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
  'image.deleted': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
  'enquiry.deleted': 'bg-[#E4002B]/5 text-[#E4002B] border-[#E4002B]/25',
  'enquiries.viewed': 'bg-[#D9D9D6]/15 text-[#53565A] border-[#D9D9D6]',
  'settings.changed': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
  'settings.content_changed': 'bg-[#24135F]/10 text-[#24135F] border-[#24135F]/20',
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

export default function AuditLog() {
  const [logs, setLogs] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;

  const load = useCallback(() => {
    api
      .get('/admin/audit', { params: { page, limit } })
      .then((res) => {
        setLogs(res.data.logs);
        setTotal(res.data.total);
      })
      .catch(() => toast.error('Failed to load audit log'));
  }, [page]);

  useEffect(load, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-full bg-[#E4002B]/10 flex items-center justify-center">
          <ShieldCheck className="h-4 w-4 text-[#E4002B]" />
        </span>
        <div>
          <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">AUDIT LOG</h1>
          <p className="text-xs text-[#53565A]/70">Record of all admin actions for security and accountability</p>
        </div>
      </div>

      <div className="bg-white border border-[#D9D9D6] rounded-xl overflow-hidden">
        {!logs ? (
          <div className="p-5 space-y-3">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-10 rounded-lg" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-sm text-[#53565A]">No audit records yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="admin-audit-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id} className="hover:bg-[#D9D9D6]/15">
                    <TableCell className="text-xs text-[#53565A] whitespace-nowrap">{formatDateTime(log.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${ACTION_STYLES[log.action] || 'bg-[#D9D9D6]/15 text-[#53565A] border-[#D9D9D6]'}`}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{log.user_email || '-'}</TableCell>
                    <TableCell className="text-xs text-[#53565A] max-w-[320px] truncate" title={log.summary}>
                      {log.summary || '-'}
                    </TableCell>
                    <TableCell className="text-xs text-[#53565A]/70">{log.ip || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#53565A]/70">
            Page {page} of {totalPages} ({total} records)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-full h-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-full h-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
